import React, { useState } from 'react';
import { motion } from 'framer-motion';
import newsHighlights from './newsHighlights.json';
import newsFeed from './newsFeed.json';
import './NewsPage.css';

const NewsPage = () => {
  const [filters, setFilters] = useState({
    club: '',
    search: '',
    tag: ''
  });
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 4;

  const allTags = [...new Set(newsFeed.flatMap(news => news.tags))];

  const filteredNews = newsFeed.filter((news) => {
    const matchesClub = filters.club ? news.club === filters.club : true;
    const matchesSearch = filters.search
      ? news.titre.toLowerCase().includes(filters.search.toLowerCase()) ||
        news.contenu.toLowerCase().includes(filters.search.toLowerCase())
      : true;
    const matchesTag = filters.tag ? news.tags.includes(filters.tag) : true;
    return matchesClub && matchesSearch && matchesTag;
  });

  const paginatedNews = filteredNews.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const resetFilters = () => {
    setFilters({ club: '', search: '', tag: '' });
    setCurrentPage(0);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNextPage = () => {
    const maxPage = Math.floor((filteredNews.length - 1) / itemsPerPage);
    setCurrentPage((prev) => Math.min(prev + 1, maxPage));
  };

  return (
    <motion.div className="news-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <div className="news-left">
        {newsHighlights.map((highlight, index) => (
          <motion.div
            className="highlight-card-compact"
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <img src={highlight.image} alt={highlight.titre} className="highlight-thumb" />
            <div className="highlight-info">
              <span className="highlight-tag">{highlight.tags[0]}</span>
              <h3 className="highlight-title">{highlight.titre}</h3>
              <p className="highlight-subtitle">{highlight.sous_titre}</p>
              {highlight.lien && <a href={highlight.lien} className="highlight-button">Découvrir</a>}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="news-right">
        <motion.div className="filters" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <input
            type="text"
            placeholder="Recherche..."
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setCurrentPage(0);
            }}
          />
          <select
            value={filters.club}
            onChange={(e) => {
              setFilters({ ...filters, club: e.target.value });
              setCurrentPage(0);
            }}
          >
            <option value="">Tous les clubs</option>
            {[...new Set(newsFeed.map(news => news.club))].map((club, i) => (
              <option key={i} value={club}>{club}</option>
            ))}
          </select>
          <select
            value={filters.tag}
            onChange={(e) => {
              setFilters({ ...filters, tag: e.target.value });
              setCurrentPage(0);
            }}
          >
            <option value="">Tous les tags</option>
            {allTags.map((tag, index) => (
              <option key={index} value={tag}>{tag}</option>
            ))}
          </select>
          <button className="reset-button" onClick={resetFilters}>❌ Réinitialiser</button>
        </motion.div>

        <div className="news-feed">
          {paginatedNews.map((news, index) => (
            <motion.div
              className="news-card"
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {news.image && <img src={news.image} alt={news.titre} className="news-image" />}
              <div className="news-content">
                <h3>{news.titre}</h3>
                <p>{news.contenu}</p>
                <span className="news-date">{news.date}</span>
                <div className="news-tags">
                  {news.tags.map((tag, i) => (
                    <span className={`tag tag-${tag.toLowerCase()}`} key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
          {filteredNews.length === 0 && (
            <motion.p className="no-results" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>Aucune actualité ne correspond à votre recherche.</motion.p>
          )}
        </div>

        {filteredNews.length > itemsPerPage && (
          <div className="pagination-controls">
            <button onClick={handlePrevPage} disabled={currentPage === 0}>← Précédent</button>
            <span>Page {currentPage + 1} / {Math.ceil(filteredNews.length / itemsPerPage)}</span>
            <button onClick={handleNextPage} disabled={(currentPage + 1) * itemsPerPage >= filteredNews.length}>Suivant →</button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NewsPage;
